import React, { useState, useEffect } from 'react';
import {
  CheckCircle2,
  ArrowRight,
  Clock,
  HelpCircle,
  Calculator,
  ChevronDown,
  ChevronUp,
  MapPin,
  Mail,
  Phone,
  GraduationCap,
  Sparkles,
  Layers,
  Award,
} from 'lucide-react';
import './StyleComponents/MaaTestingCenter.css';
import Contacts from './Components/Contacts'

export default function MaaTestingCenter() {
  const [activeMatrixTab, setActiveMatrixTab] = useState('all');
  const [openFaq, setOpenFaq] = useState(null);
  const [isContactOpen, setIsContactOpen] = useState(false);

  const addressText = '20 Washington Street, Tenafly, NJ, 07670';
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressText)}`;

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  useEffect(()=>{
        const options = {
          rootMargin: '0px',
          threshold: 0.3
        }
    
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible')
            }
          })
        }, options)
    
        const hiddenElements = document.querySelectorAll('.contacts-container')
        hiddenElements.forEach((element)=>observer.observe(element))
    
        return () => {
          observer.disconnect()
        }
    },[])

  const matrixData = [
    {
      label: 'Target Grade',
      icon: <GraduationCap size={18} className="icon-gold" />,
      amc8: 'Grades 8 and below',
      amc10: 'Grades 10 and below',
      amc12: 'Grades 12 and below'
    },
    {
      label: 'Testing Duration',
      icon: <Clock size={18} className="icon-gold" />,
      amc8: '40 minutes',
      amc10: '75 minutes',
      amc12: '75 minutes'
    },
    {
      label: 'Question Count',
      icon: <Layers size={18} className="icon-gold" />,
      amc8: '25 questions',
      amc10: '25 questions',
      amc12: '25 questions'
    },
    {
      label: 'Maximum Score',
      icon: <Award size={18} className="icon-gold" />,
      amc8: '25 points',
      amc10: '150 points',
      amc12: '150 points'
    },
    {
      label: 'Blank Questions',
      icon: <HelpCircle size={18} className="icon-gold" />,
      amc8: '0 points',
      amc10: '+1.5 points',
      amc12: '+1.5 points'
    },
    {
      label: 'Calculator Policy',
      icon: <Calculator size={18} className="icon-red" />,
      amc8: 'Strictly Prohibited',
      amc10: 'Strictly Prohibited',
      amc12: 'Strictly Prohibited',
      isDanger: true
    },
    {
      label: 'Next Stage',
      icon: <Sparkles size={18} className="icon-navy" />,
      amc8: 'Honor Roll',
      amc10: '≥ 100 Qualifies for AIME',
      amc12: '≥ 85 Qualifies for AIME',
      isHighlight: true
    }
  ];

  const faqList = [
    {
      q: 'Are any calculators or digital watches permitted in the examination hall?',
      a: 'No. In accordance with Mathematical Association of America rules, no calculators of any type, smartwatches, smartphones, or electronic measuring devices are permitted during test administration. All calculations must be performed by hand. Scrap paper and standardized pencils are supplied at each desk.'
    },
    {
      q: 'Can a student participate in both Contest A and Contest B?',
      a: 'Yes. Students may take both AMC 10A and AMC 10B, or mix examinations (such as AMC 10A and AMC 12B) if eligible by grade. Each contest offers an independent qualification index toward the AIME. Tenafly Prep hosts official sessions for both dates.'
    },
    {
      q: 'Can an accelerated middle schooler take the AMC 10 or AMC 12?',
      a: 'Yes. MAA rules specify maximum grade and age cutoffs rather than minimums. Highly qualified 7th or 8th-grade candidates are fully permitted to register and sit for the AMC 10 or AMC 12 at our center to target early AIME invitation.'
    },
    {
      q: 'What score do I need to qualify for the AIME?',
      a: 'Starting 2026 season, students taking the AMC 10 qualify for AIME with a score of 100 or higher and students taking the AMC 12 qualify for the AIME with a score of 85 or higher.'
    }
  ];

  return (
    <div className="amc-page-container">
      {/* Hero Section */}
      <section className="amc-section amc-hero-section">
        <div className="amc-max-width">
          <div className="amc-pill-tag">
            <span className="amc-dot" />
            <span className="amc-pill-text">Official Authorized Testing Center</span>
            <span className="amc-pill-divider">|</span>
            <span className="amc-pill-sub">Mathematical Association of America</span>
          </div>

          <div className="amc-hero-grid">
            <div className="amc-hero-content">
              <h1 className="amc-hero-title">
                MAA American Mathematics Competitions at Tenafly Prep
              </h1>
              <p className="amc-hero-description">
                Tenafly Prep Academy provides accredited, distraction-free in-person proctoring for AMC 8, AMC 10, and AMC 12. Testing on-site guarantees strict adherence to national competition protocols, certified verification of official scores, and direct qualification tracking toward the American Invitational Mathematics Examination (AIME) and USAMO.
              </p>

              <div className="amc-hero-actions">
                <button
                  type="button"
                  className="amc-btn amc-btn-primary"
                  onClick={() => setIsContactOpen(true)}
                >
                  Register / Contact Us <ArrowRight size={16} />
                </button>
                <a href="#matrix" className="amc-btn amc-btn-secondary">
                  View Comparison Matrix
                </a>
              </div>

              <div className="amc-trust-stats">
                <div className="amc-stat-item">
                  <span className="amc-stat-val">100%</span>
                  <span className="amc-stat-lbl">MAA Certified Proctors</span>
                </div>
                <div className="amc-stat-item">
                  <span className="amc-stat-val">AIME</span>
                  <span className="amc-stat-lbl">Direct Path Qualification</span>
                </div>
                <div className="amc-stat-item">
                  <span className="amc-stat-val">Strict</span>
                  <span className="amc-stat-lbl">Silent Hall Standards</span>
                </div>
              </div>
            </div>

            <div className="amc-hero-card-wrapper">
              <div className="amc-hero-card">
                <div className="amc-hero-img-box">
                  <img
                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuB7AjQvPMG5gxdtAOngD5hKzEOwWyvuyxyajBwXHYMDvPcKILuDvqdamCtQcbZryuLrV4bVGRdsAIM2AJQP8z6aMrB5W1pSpEawbPCX8G0-VufNEDzEgr_MfbIzVKgmVa07HdDWlJv2Q4ZzNrpWVX7sz6mtf9Y7lTbRWUHmRuXuafLZu0Yy3zw9FJIPmLbUe6cIH5DYscs71Kh5UkPufQRl9GW-N6UUp6QJ5xqO4WE90S80LIWVyc3h2Q"
                    alt="Testing Hall"
                    className="amc-hero-img"
                  />
                </div>
                <div className="amc-badge-banner">
                  <CheckCircle2 size={20} className="icon-gold flex-shrink-0" />
                  <div>
                    <h4 className="amc-badge-title">Accredited Testing Facility</h4>
                    <p className="amc-badge-desc">Tenafly, New Jersey. Paper-based testing and proctored upholding integrity under MAA guidelines.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contest Categories */}
      <section className="amc-section amc-categories-section">
        <div className="amc-max-width">
          <div className="amc-section-header">
            <span className="amc-section-kicker">Divisions & Architecture</span>
            <h2 className="amc-section-title">Official Contest Categories</h2>
            <p className="amc-section-sub">
              Choose the appropriate division based on grade level and mathematics trajectory. Both examinations follow national MAA parameters.
            </p>
          </div>

          <div className="amc-cards-grid">
            {/* Card 1 */}
            <div className="amc-category-card">
              <div className="amc-card-top">
                <div className="amc-card-badge-row">
                  <span className="amc-badge-pill">Middle School Division</span>
                  <span className="amc-card-date">January</span>
                </div>
                <h3 className="amc-card-title">AMC 8 Competition</h3>
                <p className="amc-card-eligibility">Eligible: Grades 8 and below (under 15.5 years)</p>

                <div className="amc-card-specs">
                  <div className="amc-spec-row">
                    <span className="amc-spec-lbl"><Clock size={16} className="icon-navy" /> Time Limit</span>
                    <span className="amc-spec-val">40 Minutes</span>
                  </div>
                  <div className="amc-spec-row">
                    <span className="amc-spec-lbl"><Layers size={16} className="icon-navy" /> Questions</span>
                    <span className="amc-spec-val">25 Multiple Choice</span>
                  </div>
                  <div className="amc-spec-row">
                    <span className="amc-spec-lbl"><Calculator size={16} className="icon-navy" /> Scoring Model</span>
                    <span className="amc-spec-val">+1.0 per correct • Zero penalty</span>
                  </div>
                </div>

                <p className="amc-card-body">
                  Designed to promote problem-solving acumen and analytical thinking. Topics include elementary number theory, spatial geometry, and pre-algebra concepts.
                </p>
              </div>

              <div className="amc-card-bottom">
                <span className="amc-card-cap">Seat capacity: 12</span>
                <button
                  type="button"
                  className="amc-card-link"
                  onClick={() => setIsContactOpen(true)}
                >
                  Register AMC 8 <ArrowRight size={14} />
                </button>
              </div>
            </div>

            {/* Card 2 */}
            <div className="amc-category-card">
              <div className="amc-card-top">
                <div className="amc-card-badge-row">
                  <span className="amc-badge-pill">High School & AIME Gateway</span>
                  <span className="amc-card-date">November</span>
                </div>
                <h3 className="amc-card-title">AMC 10 & AMC 12</h3>
                <p className="amc-card-eligibility">Eligible: AMC 10 (Grades ≤ 10) • AMC 12 (Grades ≤ 12)</p>

                <div className="amc-card-specs">
                  <div className="amc-spec-row">
                    <span className="amc-spec-lbl"><Clock size={16} className="icon-navy" /> Time Limit</span>
                    <span className="amc-spec-val">75 Minutes</span>
                  </div>
                  <div className="amc-spec-row">
                    <span className="amc-spec-lbl"><Layers size={16} className="icon-navy" /> Questions</span>
                    <span className="amc-spec-val">25 Multiple Choice</span>
                  </div>
                  <div className="amc-spec-row">
                    <span className="amc-spec-lbl"><Calculator size={16} className="icon-navy" /> Scoring Model</span>
                    <span className="amc-spec-val">+6.0 correct • +1.5 blank • 0 wrong</span>
                  </div>
                </div>

                <p className="amc-card-body">
                  The nation's benchmark high school mathematics examinations. Top-performing participants directly receive an official invitation to sit for the AIME.
                </p>
              </div>

              <div className="amc-card-bottom">
                <span className="amc-card-cap">Seat capacity: 12</span>
                <button
                  type="button"
                  className="amc-card-link"
                  onClick={() => setIsContactOpen(true)}
                >
                  Register AMC 10 / 12 <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Matrix Section */}
      <section className="amc-section amc-matrix-section" id="matrix">
        <div className="amc-max-width">
          <div className="amc-section-header amc-text-center">
            <span className="amc-section-kicker">Side-by-Side Analysis</span>
            <h2 className="amc-section-title">Competition Comparison Matrix</h2>
            <p className="amc-section-sub">
              Detailed specifications across all MAA examination tiers held at Tenafly Prep Academy.
            </p>
          </div>

          <div className="amc-tabs-bar">
            {['all', 'amc8', 'amc10', 'amc12'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveMatrixTab(tab)}
                className={`amc-tab-btn ${activeMatrixTab === tab ? 'active' : ''}`}
              >
                {tab === 'all' ? 'View All Columns' : tab.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="amc-table-container">
            <table className="amc-table">
              <thead>
                <tr>
                  <th>  </th>
                  {(activeMatrixTab === 'all' || activeMatrixTab === 'amc8') && <th>AMC 8</th>}
                  {(activeMatrixTab === 'all' || activeMatrixTab === 'amc10') && <th>AMC 10 (A/B)</th>}
                  {(activeMatrixTab === 'all' || activeMatrixTab === 'amc12') && <th>AMC 12 (A/B)</th>}
                </tr>
              </thead>
              <tbody>
                {matrixData.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`
                      ${row.isDanger ? 'row-danger' : ''} 
                      ${row.isHighlight ? 'row-highlight' : ''}
                    `}
                  >
                    <td className="amc-td-label">
                      {row.icon}
                      <span>{row.label}</span>
                    </td>
                    {(activeMatrixTab === 'all' || activeMatrixTab === 'amc8') && (
                      <td className={row.isDanger ? 'text-danger' : row.isHighlight ? 'text-navy-bold' : ''}>
                        {row.amc8}
                      </td>
                    )}
                    {(activeMatrixTab === 'all' || activeMatrixTab === 'amc10') && (
                      <td className={row.isDanger ? 'text-danger' : row.isHighlight ? 'text-navy-bold' : ''}>
                        {row.amc10}
                      </td>
                    )}
                    {(activeMatrixTab === 'all' || activeMatrixTab === 'amc12') && (
                      <td className={row.isDanger ? 'text-danger' : row.isHighlight ? 'text-navy-bold' : ''}>
                        {row.amc12}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="amc-section amc-faq-section" id="faq">
        <div className="amc-max-width amc-max-width-narrow">
          <div className="amc-section-header">
            <span className="amc-section-kicker">Candidate & Parent Guidance</span>
            <h2 className="amc-section-title">Frequently Asked Questions</h2>
            <p className="amc-section-sub">
              Essential information regarding site regulations, scheduling, and student qualifications.
            </p>
          </div>

          <div className="amc-faq-list">
            {faqList.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div key={index} className={`amc-faq-item ${isOpen ? 'open' : ''}`}>
                  <button
                    className="amc-faq-trigger"
                    onClick={() => toggleFaq(index)}
                    aria-expanded={isOpen}
                  >
                    <span className="amc-faq-question">
                      <span className="amc-dot-gold" />
                      {faq.q}
                    </span>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>

                  <div className={`amc-faq-content-wrapper ${isOpen ? 'expanded' : ''}`}>
                    <div className="amc-faq-content">
                      <p>{faq.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Registration Contact Popup */}
      {isContactOpen && (
        <div
          className="amc-modal-overlay"
          role="presentation"
          onClick={() => setIsContactOpen(false)}
        >
          <div
            className="amc-contact-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="amc-contact-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="amc-modal-close"
              onClick={() => setIsContactOpen(false)}
              aria-label="Close registration contact popup"
            >
              ×
            </button>

            <span className="amc-section-kicker">Registration & Contact</span>
            <h2 id="amc-contact-title" className="amc-contact-title">
              Ready to Register?
            </h2>
            <p className="amc-contact-desc">
              Please contact Tenafly Prep Academy to register for an AMC testing session
              or to ask about available dates and registration details.
            </p>

            <div className="amc-contact-details">
              <div className="amc-contact-item">
                <MapPin size={20} className="icon-gold" />
                <div>
                  <strong>Testing Location</strong>
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                    {addressText}
                  </a>
                </div>
              </div>

              <div className="amc-contact-item">
                <Mail size={20} className="icon-gold" />
                <div>
                  <strong>Email</strong>
                  <a href="mailto:tenaflyacademy26@gmail.com">tenaflyacademy26@gmail.com</a>
                </div>
              </div>

              <div className="amc-contact-item">
                <Phone size={20} className="icon-gold" />
                <div>
                  <strong>Phone</strong>
                  <a href="tel:+12013902381">(201)-390-2381</a>
                </div>
              </div>
            </div>

            <div className="amc-contact-actions">
              <a
                href="mailto:tenaflyacademy26@gmail.com"
                className="amc-btn amc-btn-primary"
              >
                <Mail size={16} />
                Email Us
              </a>
              <a
                href="tel:+12013902381"
                className="amc-btn amc-btn-secondary"
              >
                <Phone size={16} />
                Call Us
              </a>
              <button
                type="button"
                className="amc-btn amc-btn-secondary amc-contact-close-btn"
                onClick={() => setIsContactOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <Contacts/> 
    </div>
  );
}